import { useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useFetch } from "hooks/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api/index";
import { successToast, errorToast } from "utils/toasts";
import { Button } from "components/core/basic/Button";
import CenteredModal from "components/core/basic/CenteredModal";
import AmazonProfilesTable from "./AmazonProfilesTable";
import { useAppSelector } from "redux/hooks";
import AddAmazonProfilesDialogue from "./AddAmazonProfilesDialogue";
import { useAmazonProfiles } from "./useAmazonProfiles";
import { useHistory } from "react-router-dom";
import AMCInstanceSection from "./AMCInstanceSection";
import MissingProfilesSections from "./MissingProfilesSections";

const AmazonProfileSummary = () => {
  const [fetchRemoteProfiles, setFetchRemoteProfiles] = useState(false);
  const [showAdditionalProfilesModal, setShowAdditionalProfilesModal] = useState(false);

  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);
  const queryClient = useQueryClient();
  const history = useHistory();

  if (history.location.state?.sendToastNotification) {
    const notificationType = history.location.state.sendToastNotification;

    if (notificationType === "additionalAccountSuccessfullyAdded") {
      setTimeout(() => {
        successToast({
          message: "New Amazon Ads account successfully authorized.",
        });
      }, 2000);
    }

    if (notificationType === "reauthSuccess") {
      setTimeout(() => {
        successToast({
          message: "Amazon Ads account successfully reauthorized.",
        });
      }, 2000);
      history.replace();
    }
  }

  if (history.location.state?.showProfileImportModal && !showAdditionalProfilesModal) {
    history.replace();

    setShowAdditionalProfilesModal(true);
  }

  const {
    formattedRemoteProfilesData,
    groupedProfilesByClient,
    remoteProfilesData,
    isLoading: isRemoteProfileDataLoading,
  } = useAmazonProfiles(false, fetchRemoteProfiles || showAdditionalProfilesModal);

  const remoteProfilesTableData = useMemo(() => {
    const combinedData = formattedRemoteProfilesData?.map((remoteProfile) => {
      const clientInfo = groupedProfilesByClient.find((client) => {
        return client.profiles?.map((p) => p.entity_id)?.includes(remoteProfile.entity_id);
      });
      const { amazon_user_email, amazon_user_name, amazon_user_id } = clientInfo ?? {};

      return { ...remoteProfile, amazon_user_email, amazon_user_id, amazon_user_name };
    });
    return combinedData;
  }, [groupedProfilesByClient, formattedRemoteProfilesData]);

  const { data: trellisProfilesData, trellisProfilesLoading } = useFetch(
    [channel, user.organization_id, "trellisProfiles"],
    "/api/organizations/",
    { page_size: 5000 },
    {
      select: (d) => d.data?.filter((profile) => profile.org_type === "AMAZON"),
      default: [],
    },
  );

  const importProfiles = useMutation({
    mutationFn: async (profileList) => {
      return await api.post("/api/manage/refresh_profiles/", {
        org: parseInt(user.organization_id),
        selected_profiles: profileList,
      });
    },

    onSuccess: () => {
      successToast({
        message: `Successfully Refreshed Amazon Profiles`,
      });
      setShowAdditionalProfilesModal(false);
      // Below is hacky, but locally celery task took a few seconds to run before new orgs/profiles created
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [channel, user.organization_id, "trellisProfiles"],
        });
      }, 10000);
    },

    onError: (error) => {
      errorToast({ message: `Error Refreshing Amazon Profiles`, error });
    },
  });
  return (
    <>
      <Row className="text-start mx-0 w-100" style={{ color: "#393939" }}>
        <Col xs={12} lg={9} className="px-0">
          <h2 style={{ fontSize: "28px", fontWeight: 600 }}>Trellis Profile Summary</h2>
          <p className="fs-3 mb-5">
            Below is a summary of all Amazon profiles currently connected to your Trellis account.
          </p>
        </Col>

        <Col xs={12} lg={3} className="d-flex justify-content-end align-items-center px-0">
          <CenteredModal
            searchModal={groupedProfilesByClient?.flatMap((client) => client.profiles)?.length}
            show={showAdditionalProfilesModal}
            closeModal={() => {
              setShowAdditionalProfilesModal(false);
            }}
            title="Import Additional Amazon Profiles"
            content={
              <AddAmazonProfilesDialogue
                setShowModal={setShowAdditionalProfilesModal}
                onSubmit={(selectedProfiles) => {
                  const profilesToImport = selectedProfiles?.map((selectedProfileId) => {
                    let selectedProfile = null;
                    if (groupedProfilesByClient?.length) {
                      selectedProfile = groupedProfilesByClient
                        ?.flatMap((client) => client.profiles)
                        ?.find((remoteProfile) => remoteProfile.profile_id === selectedProfileId);
                      return { name: selectedProfile.profile_name, type: selectedProfile.profile_type };
                    } else {
                      selectedProfile = remoteProfilesData?.find(
                        (remoteProfile) => remoteProfile.profileId === selectedProfileId,
                      );
                      return { name: selectedProfile.accountInfo.name, type: selectedProfile.accountInfo.type };
                    }
                  });

                  importProfiles.mutate(profilesToImport);
                }}
              />
            }
          />
          <Button
            disabled={importProfiles.isPending}
            onPress={() => {
              setShowAdditionalProfilesModal(true);
            }}
          >
            Add Profiles
          </Button>
        </Col>
      </Row>
      <AmazonProfilesTable
        tableData={trellisProfilesData}
        columnsToRender={["name", "marketplaces", "profile_type", "status"]}
        isLoading={trellisProfilesLoading}
      />
      <Row className="text-start mx-0 w-100" style={{ color: "#393939" }}>
        <Col xs={12} lg={9} className="px-0">
          <h2 style={{ fontSize: "28px", fontWeight: 600 }}>Amazon Profile Summary</h2>
          <p className="fs-3 mb-5">
            Click the button to the right to see all profiles from Amazon associated with your Amazon Advertising
            Account.
          </p>
        </Col>
        <Col xs={12} lg={3} className="d-flex justify-content-end align-items-center px-0">
          <Button
            onPress={() => {
              setFetchRemoteProfiles(!fetchRemoteProfiles);
            }}
            isPending={isRemoteProfileDataLoading}
          >
            {isRemoteProfileDataLoading ? "Loading Amazon Profiles" : "Check Amazon Profiles"}
          </Button>
        </Col>
      </Row>
      {fetchRemoteProfiles && (
        <>
          <AmazonProfilesTable
            tableData={remoteProfilesTableData}
            columnsToRender={[
              "marketplaces",
              "profile_type",
              "amazon_user_email",
              "amazon_user_id",
              "amazon_user_name",
              "details",
            ]}
            isLoading={isRemoteProfileDataLoading}
          />
          <p className="fs-2 mt-5 mb-2 w-100" style={{ fontWeight: 600 }}>
            Not seeing your profile from Amazon?
          </p>
          <p className="fs-3 mb-5 w-75">
            At least one campaign has to have been made in the marketplace for Amazon to share the profile.
            Additionally, the Amazon account connected to the Trellis platform must have "View & Edit" permissions to
            "Campaign Manager"
          </p>
        </>
      )}
      {user.is_staff ?
        <>
          <AMCInstanceSection />
          <MissingProfilesSections />
        </>
      : null}
    </>
  );
};

export default AmazonProfileSummary;
